import type { CompiledMesh } from '../../chunk-schema/src/index.ts'

export type IndexedSurfaceKind = 'road' | 'junction'

export type SurfaceHeightHit = {
  readonly meshId: string
  readonly kind: IndexedSurfaceKind
  readonly triangleIndex: number
  readonly height: number
}

type IndexedSurfaceTriangle = Omit<SurfaceHeightHit, 'height'> & {
  readonly ax: number
  readonly ay: number
  readonly az: number
  readonly bx: number
  readonly by: number
  readonly bz: number
  readonly cx: number
  readonly cy: number
  readonly cz: number
  readonly denominator: number
}

const DEFAULT_CELL_SIZE_METERS = 12
const HORIZONTAL_AREA_EPSILON = 1e-8
const CONTAINMENT_EPSILON = 1e-4
const HEIGHT_TIE_EPSILON = 1e-7

function isIndexedSurface(mesh: CompiledMesh): mesh is CompiledMesh & { readonly kind: IndexedSurfaceKind } {
  return (mesh.kind === 'road' || mesh.kind === 'junction') && mesh.topology === 'triangles'
}

function cellKey(cellX: number, cellZ: number): string {
  return `${cellX}:${cellZ}`
}

function ownerPriority(kind: IndexedSurfaceKind): number {
  return kind === 'junction' ? 0 : 1
}

function winsDeterministicTie(candidate: IndexedSurfaceTriangle, incumbent: SurfaceHeightHit): boolean {
  const priorityDelta = ownerPriority(candidate.kind) - ownerPriority(incumbent.kind)
  if (priorityDelta !== 0) return priorityDelta < 0
  const meshDelta = candidate.meshId.localeCompare(incumbent.meshId, 'en')
  return meshDelta < 0 || (meshDelta === 0 && candidate.triangleIndex < incumbent.triangleIndex)
}

/**
 * Incremental XZ acceleration structure for the exact road and junction
 * triangles mounted by one ChunkInstance. Runtime support therefore comes from
 * the same Float32 positions that Three.js renders, without scanning the whole
 * chunk once per wheel and physics substep.
 */
export class SurfaceTriangleHeightIndex {
  private readonly cells = new Map<string, IndexedSurfaceTriangle[]>()
  private readonly indexedMeshIds = new Set<string>()
  private readonly cellSizeMeters: number

  constructor(cellSizeMeters = DEFAULT_CELL_SIZE_METERS) {
    if (!Number.isFinite(cellSizeMeters) || cellSizeMeters <= 0) {
      throw new Error(`Surface triangle index cell size must be positive, received ${cellSizeMeters}.`)
    }
    this.cellSizeMeters = cellSizeMeters
  }

  addMesh(mesh: CompiledMesh): number {
    if (!isIndexedSurface(mesh) || this.indexedMeshIds.has(mesh.id)) return 0
    this.indexedMeshIds.add(mesh.id)
    let indexedTriangles = 0
    for (let offset = 0; offset < mesh.indices.length; offset += 3) {
      const a = mesh.indices[offset] * 3
      const b = mesh.indices[offset + 1] * 3
      const c = mesh.indices[offset + 2] * 3
      const ax = mesh.positions[a]
      const ay = mesh.positions[a + 1]
      const az = mesh.positions[a + 2]
      const bx = mesh.positions[b]
      const by = mesh.positions[b + 1]
      const bz = mesh.positions[b + 2]
      const cx = mesh.positions[c]
      const cy = mesh.positions[c + 1]
      const cz = mesh.positions[c + 2]
      const denominator = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz)
      if (![ax, ay, az, bx, by, bz, cx, cy, cz, denominator].every(Number.isFinite)) continue
      if (Math.abs(denominator) < HORIZONTAL_AREA_EPSILON) continue
      const triangle: IndexedSurfaceTriangle = {
        meshId: mesh.id,
        kind: mesh.kind,
        triangleIndex: offset / 3,
        ax,
        ay,
        az,
        bx,
        by,
        bz,
        cx,
        cy,
        cz,
        denominator,
      }
      const minCellX = Math.floor((Math.min(ax, bx, cx) - CONTAINMENT_EPSILON) / this.cellSizeMeters)
      const maxCellX = Math.floor((Math.max(ax, bx, cx) + CONTAINMENT_EPSILON) / this.cellSizeMeters)
      const minCellZ = Math.floor((Math.min(az, bz, cz) - CONTAINMENT_EPSILON) / this.cellSizeMeters)
      const maxCellZ = Math.floor((Math.max(az, bz, cz) + CONTAINMENT_EPSILON) / this.cellSizeMeters)
      for (let cellX = minCellX; cellX <= maxCellX; cellX += 1) {
        for (let cellZ = minCellZ; cellZ <= maxCellZ; cellZ += 1) {
          const key = cellKey(cellX, cellZ)
          const bucket = this.cells.get(key)
          if (bucket) bucket.push(triangle)
          else this.cells.set(key, [triangle])
        }
      }
      indexedTriangles += 1
    }
    return indexedTriangles
  }

  sample(localX: number, localZ: number, referenceHeight: number): SurfaceHeightHit | undefined {
    if (![localX, localZ, referenceHeight].every(Number.isFinite)) return undefined
    const bucket = this.cells.get(cellKey(
      Math.floor(localX / this.cellSizeMeters),
      Math.floor(localZ / this.cellSizeMeters),
    ))
    if (!bucket) return undefined
    let best: SurfaceHeightHit | undefined
    let bestHeightDelta = Number.POSITIVE_INFINITY
    for (const triangle of bucket) {
      const wa = ((triangle.bz - triangle.cz) * (localX - triangle.cx)
        + (triangle.cx - triangle.bx) * (localZ - triangle.cz)) / triangle.denominator
      const wb = ((triangle.cz - triangle.az) * (localX - triangle.cx)
        + (triangle.ax - triangle.cx) * (localZ - triangle.cz)) / triangle.denominator
      const wc = 1 - wa - wb
      if (wa < -CONTAINMENT_EPSILON || wb < -CONTAINMENT_EPSILON || wc < -CONTAINMENT_EPSILON) continue
      const height = triangle.ay * wa + triangle.by * wb + triangle.cy * wc
      const heightDelta = Math.abs(height - referenceHeight)
      if (best && heightDelta > bestHeightDelta + HEIGHT_TIE_EPSILON) continue
      if (best && Math.abs(heightDelta - bestHeightDelta) <= HEIGHT_TIE_EPSILON
        && !winsDeterministicTie(triangle, best)) continue
      bestHeightDelta = heightDelta
      best = {
        meshId: triangle.meshId,
        kind: triangle.kind,
        triangleIndex: triangle.triangleIndex,
        height,
      }
    }
    return best
  }

  clear(): void {
    this.cells.clear()
    this.indexedMeshIds.clear()
  }
}
